/**
 * Inline-picker — singleton-overlay для выбора одного элемента из списка.
 *
 * Phase 2 Wave 3: заменяет `prompt()`-based выбор в Project Inspector
 * (назначение Менеджеров/Пользователей) и admin (привязка contractor к user).
 *
 * Архитектура (правки после критики Agent4):
 * - **Б1: singleton.** Один экземпляр на module-init. document-level listeners
 *   (pointerdown, keydown) регистрируются ОДИН раз. Не накапливаются при
 *   повторном открытии Inspector'а в разных проектах.
 * - **Б2: position:absolute в body.** НЕ внутри innerHTML, чтобы render()
 *   родительского компонента не уничтожал picker DOM.
 * - **С5: generic.** renderItem(item)+filterText(item) — компонент не знает
 *   про users vs contractors, переиспользуется для обоих.
 *
 * @returns {{ element: HTMLElement, open: Function, close: Function }}
 */
export function createInlinePicker() {
  const panel = document.createElement('div');
  panel.className = 'inline-picker';
  panel.hidden = true;

  const search = document.createElement('input');
  search.type = 'text';
  search.className = 'ip-input';
  panel.appendChild(search);

  const list = document.createElement('div');
  list.className = 'ip-list';
  panel.appendChild(list);

  const empty = document.createElement('div');
  empty.className = 'ip-empty';
  empty.hidden = true;
  panel.appendChild(empty);

  // Текущая конфигурация (перезаписывается на каждый open).
  let config = null;
  let filtered = [];

  function rerender() {
    if (!config) return;
    const q = search.value.trim().toLowerCase();
    filtered = q
      ? config.items.filter(it => config.filterText(it).toLowerCase().includes(q))
      : config.items.slice();
    list.innerHTML = filtered
      .map((it, i) => `<button type="button" class="ip-item" data-index="${i}">${config.renderItem(it)}</button>`)
      .join('');
  }

  // Click-delegation на items.
  list.addEventListener('click', e => {
    const btn = e.target.closest('button[data-index]');
    if (!btn || !config) return;
    const idx = Number(btn.dataset.index);
    const item = filtered[idx];
    if (!item) return;
    const onPick = config.onPick;
    close();
    onPick(item);
  });

  // Search input → live-filter.
  search.addEventListener('input', rerender);

  // Stop propagation внутри panel — иначе pointerdown closes сам себя.
  panel.addEventListener('pointerdown', e => e.stopPropagation());

  // Singleton document-listeners (Б1) — регистрируются ОДИН раз.
  document.addEventListener('pointerdown', e => {
    if (panel.hidden) return;
    if (config && config.anchor && config.anchor.contains(e.target)) return;
    if (panel.contains(e.target)) return;
    cancel();
  }, true);

  document.addEventListener('keydown', e => {
    if (panel.hidden) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  });

  function position() {
    if (!config || !config.anchor) return;
    const rect = config.anchor.getBoundingClientRect();
    const panelW = 280;
    let left = rect.left + window.scrollX;
    let top = rect.bottom + window.scrollY + 4;
    // Корректировка чтобы не вылезть за правый край viewport'а.
    const viewportW = document.documentElement.clientWidth;
    if (left + panelW > viewportW - 8) left = viewportW - panelW - 8;
    panel.style.left = `${Math.max(8, left)}px`;
    panel.style.top = `${top}px`;
  }

  function open(cfg) {
    config = cfg;
    search.placeholder = cfg.placeholder || 'Поиск…';
    search.value = '';

    if (!cfg.items || cfg.items.length === 0) {
      list.hidden = true;
      search.hidden = true;
      empty.hidden = false;
      empty.textContent = cfg.emptyMessage || 'Нет элементов.';
    } else {
      list.hidden = false;
      search.hidden = false;
      empty.hidden = true;
      rerender();
    }

    panel.hidden = false;
    position();
    if (!search.hidden) {
      // Фокус с задержкой — иначе pointerdown на якоре всё ещё в обработке.
      setTimeout(() => search.focus(), 0);
    }
  }

  function close() {
    panel.hidden = true;
    config = null;
    filtered = [];
  }

  function cancel() {
    const onCancel = config && config.onCancel;
    close();
    if (onCancel) onCancel();
  }

  return {
    element: panel,
    open,
    close,
  };
}
